import type { UseSlideInProps } from '@pearlconvert/ui/components/slide-in/slide-in.types'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useIsScrolled } from '@maestro/commerce-blocks/hooks/use-is-scrolled'
import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import { EventType } from '@maestro/data-layer/models/event'
import { useHash } from '@pearlconvert/ui/hooks/use-hash'
import { useDisabledPageScroll } from '@pearlconvert/ui/providers/disabled-page-scroll/disabled-page-scroll.context'

// Shared ref to store the original trigger element across all slide-in instances
let sharedTriggerElement: HTMLElement | null = null

export const useSlideIn = (props: UseSlideInProps) => {
  const {
    hash,
    isOpen,
    onClose,
    onOpen,
    title,
    slideInType,
    toastContainerRef,
    scrollToTop,
    modalId,
    openModals = [],
  } = props

  const { publish } = useEventBusContext()
  const dialogRef = useRef<HTMLDialogElement | null>(null)
  const dialogContentRef = useRef<HTMLDivElement | null>(null)
  const titleRef = useRef<HTMLHeadingElement | null>(null)
  const hasInitiallyFocusedTitle = useRef(false)
  const { toggleDisabledPageScroll } = useDisabledPageScroll()
  const { getHashParamValue, popHash, clearHash } = useHash()

  const hasHash = hash !== undefined
  const hashValue = hasHash ? getHashParamValue(hash) : undefined
  const hasHashValue = hashValue !== undefined

  const isTopmostModal = useMemo(() => {
    return modalId === openModals.at(-1)
  }, [modalId, openModals])

  const stackIndex = useMemo(() => {
    if (modalId === undefined) {
      return 0
    }
    return 2 * Math.max(0, openModals?.indexOf(modalId) ?? 0)
  }, [modalId, openModals])

  const hasOpenedRef = useRef(false)
  const hasClosedRef = useRef(false)

  const hasTrackedCloseRef = useRef(false)

  useEffect(() => {
    if (isOpen) {
      hasTrackedCloseRef.current = false
    }
  }, [isOpen])

  const closeAndTrack = useCallback(() => {
    // a single close can reach several handlers (e.g. click handler, then hash
    // removal re-triggering the effect), so track it once per opening
    if (!hasTrackedCloseRef.current) {
      hasTrackedCloseRef.current = true
      publish(EventType.SLIDE_IN_CLOSED, {
        title: typeof title === 'string' ? title : '',
        slideInType: slideInType ?? (hasHashValue ? hash : modalId),
        action: 'close',
      })
    }
    onClose?.()
  }, [hash, hasHashValue, modalId, onClose, publish, slideInType, title])

  useEffect(() => {
    if (isOpen && !hasHash && !hasOpenedRef.current) {
      onOpen?.()
      hasOpenedRef.current = true
      hasClosedRef.current = false
      return
    }

    if (!isOpen && hasHash && hasHashValue && !hasOpenedRef.current) {
      onOpen?.()
      hasOpenedRef.current = true
      hasClosedRef.current = false
      return
    }

    if (hasHash && !hasHashValue && hasOpenedRef.current) {
      closeAndTrack()
      hasClosedRef.current = true
      hasOpenedRef.current = false
      return
    }

    // case to trigger onClose callback for slide in without hash
    if (!isOpen && !hasHash && hasOpenedRef.current && !hasClosedRef.current) {
      closeAndTrack()
      hasClosedRef.current = true
      hasOpenedRef.current = false
      return
    }
  }, [hasHash, isOpen, hashValue, hasHashValue, onOpen, closeAndTrack, modalId])

  const handleClickOutside = useCallback(
    (event: Event) => {
      if (!isOpen || !isTopmostModal) {
        return
      }
      const target = event.target as HTMLElement

      if (
        !dialogRef.current?.contains(target) &&
        !toastContainerRef?.current?.contains(target)
      ) {
        if (hasHash && hasHashValue) {
          popHash()
        }
        hasClosedRef.current = true
        hasOpenedRef.current = false
        closeAndTrack()
      }
    },
    [
      isOpen,
      isTopmostModal,
      dialogRef,
      toastContainerRef,
      hasHash,
      hasHashValue,
      popHash,
      closeAndTrack,
    ]
  )

  const handleScrollOnBackdrop = useCallback(
    (event: WheelEvent): void => {
      dialogRef.current!.scrollTop += event.deltaY
    },
    [dialogRef]
  )

  const isScrolled = useIsScrolled({ target: dialogRef })

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen || !isTopmostModal) {
        return
      }
      if (event.key === 'Escape') {
        if (hasHash && hasHashValue) {
          popHash()
        }
        hasClosedRef.current = true
        hasOpenedRef.current = false
        closeAndTrack()
        return
      }
    },
    [isOpen, isTopmostModal, hasHash, hasHashValue, closeAndTrack, popHash]
  )

  // no-scroll fix for mobile Safari, check if there's an overflow in dialog
  const handleTouchMove = useCallback((e: TouchEvent) => {
    const contentElement = dialogContentRef.current
    const dialogElement = dialogRef.current

    if (!contentElement || !dialogElement) {
      return
    }

    if (contentElement.scrollHeight <= dialogElement.offsetHeight) {
      e.preventDefault()
    }
  }, [])

  const [hasBeenOpened, setHasBeenOpened] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setHasBeenOpened(true)
    }
  }, [isOpen])

  useEffect(() => {
    const dialog = dialogRef.current

    if (dialog) {
      dialog.classList.toggle('slidein--open', isOpen)

      if (isOpen) {
        // Store the currently focused element before opening, but only for the first modal
        // This ensures we return to the original trigger (e.g., user icon) not an intermediate modal
        // Check if this is the first modal being opened (no other modals open yet)
        if (openModals.length === 0 || !sharedTriggerElement) {
          sharedTriggerElement = document.activeElement as HTMLElement
        }

        dialog.show()
        document.addEventListener('keydown', handleKeyDown)
        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('touchstart', handleClickOutside)
        document.body.addEventListener('wheel', handleScrollOnBackdrop)
        dialog.addEventListener('touchmove', handleTouchMove, {
          passive: false,
        })
        toggleDisabledPageScroll(true)

        // Set inert on main content to prevent screen readers from reading background
        const mainContent = document.querySelector('main')
        const header = document.querySelector('header')
        if (mainContent) {
          mainContent.setAttribute('inert', '')
        }
        if (header) {
          header.setAttribute('inert', '')
        }

        if (scrollToTop) {
          dialog.scrollTo({ top: 0 })
        }

        // Focus the title for screen reader announcement, but only on initial open
        if (!hasInitiallyFocusedTitle.current) {
          setTimeout(() => {
            titleRef.current?.focus()
          }, 100)
          hasInitiallyFocusedTitle.current = true
        }
      } else {
        dialog.close()
        hasInitiallyFocusedTitle.current = false

        // Remove inert from main content
        const mainContent = document.querySelector('main')
        const header = document.querySelector('header')
        if (mainContent) {
          mainContent.removeAttribute('inert')
        }
        if (header) {
          header.removeAttribute('inert')
        }

        // Return focus to the element that triggered the modal, but only when all modals are closed
        if (openModals.length === 0) {
          sharedTriggerElement?.focus?.()
          // Clear the shared trigger after restoring focus
          sharedTriggerElement = null
        }
      }
    }

    return () => {
      document.body.removeEventListener('wheel', handleScrollOnBackdrop)
      document?.removeEventListener('mousedown', handleClickOutside)
      document?.removeEventListener('touchstart', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)

      if (dialog) {
        dialog.removeEventListener('touchmove', handleTouchMove)
      }

      // Clean up inert attributes on unmount
      const mainContent = document.querySelector('main')
      const header = document.querySelector('header')
      if (mainContent) {
        mainContent.removeAttribute('inert')
      }
      if (header) {
        header.removeAttribute('inert')
      }
    }
  }, [
    isOpen,
    handleClickOutside,
    handleKeyDown,
    handleScrollOnBackdrop,
    handleTouchMove,
    toggleDisabledPageScroll,
    scrollToTop,
    openModals,
  ])

  const handleClose = useCallback(() => {
    if (hasHash && hasHashValue) {
      clearHash()
    }
    hasClosedRef.current = true
    hasOpenedRef.current = false
    closeAndTrack()
  }, [clearHash, closeAndTrack, hasHash, hasHashValue])

  const handleBackButton = useCallback(() => {
    if (!isTopmostModal) {
      return
    }
    if (hasHash && hasHashValue) {
      popHash()
    }
    hasClosedRef.current = true
    hasOpenedRef.current = false
    closeAndTrack()
  }, [hasHash, hasHashValue, isTopmostModal, closeAndTrack, popHash])

  return {
    dialogRef,
    dialogContentRef,
    titleRef,
    hasBeenOpened,
    handleClose,
    handleBackButton,
    isScrolled,
    isTopmostModal,
    stackIndex,
  }
}
